import { Injectable, signal, computed } from '@angular/core';

/**
 * Tracks PWA installability and provides `beforeinstallprompt` handling.
 *
 * Uses `signal` for reactive state — consumers can use `canInstall()`
 * in templates or computed expressions to show/hide an install button.
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  readonly canInstall = signal<boolean>(false);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', (event: Event) => {
        event.preventDefault();
        this.deferredPrompt = event as BeforeInstallPromptEvent;
        this.canInstall.set(true);
      });

      window.addEventListener('appinstalled', () => {
        this.canInstall.set(false);
        this.deferredPrompt = null;
      });
    }
  }

  /** Returns true when the app is running in standalone/installed mode. */
  isRunningStandalone(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return (
      (window.navigator as { standalone?: boolean }).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    );
  }

  /**
   * Returns true on iOS/iPadOS, where Safari never fires `beforeinstallprompt`
   * and there is no programmatic install API — "Add to Home Screen" is a
   * manual Share-sheet action only. iPadOS 13+ reports as `MacIntel` in the
   * UA string, so it's distinguished from a real Mac via touch support.
   */
  isIos(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }
    const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIpadOS13Up = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return isIosDevice || isIpadOS13Up;
  }

  /** Prompts the user to install the PWA. Returns true if installed. */
  async install(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }

    this.deferredPrompt.prompt();
    const result = await this.deferredPrompt.userChoice;
    this.deferredPrompt = null;
    this.canInstall.set(false);

    return result.outcome === 'accepted';
  }
}

/**
 * Minimal type for the BeforeInstallPromptEvent which is not in the DOM lib.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}